import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Inbox,
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  MapPin,
  Bed,
  Calendar,
  ChevronRight,
  CheckCircle2,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { LeadStatus } from '@pms/shared';
import {
  usePlatformLeads,
  useApproveLead,
  useRequestLeadChanges,
  useRejectLead,
} from '@/hooks/api/usePlatform';
import type { PlatformLead, ApproveLeadResult } from '@/services/api/platform.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { extractErrorMessage } from '@/lib/error';
import { cn } from '@/lib/utils';

const STATUS_TABS: { value: LeadStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: LeadStatus.NEW, label: 'New' },
  { value: LeadStatus.CONTACTED, label: 'Contacted' },
  { value: LeadStatus.ONBOARDED, label: 'Onboarded' },
  { value: LeadStatus.CLOSED, label: 'Closed' },
];

const STATUS_BADGE: Record<LeadStatus, string> = {
  // Pending (unverified) leads are excluded from the admin queue, but the maps
  // must stay exhaustive over LeadStatus.
  [LeadStatus.PENDING_VERIFICATION]: 'bg-gray-100 text-gray-400',
  [LeadStatus.NEW]: 'bg-blue-100 text-blue-700',
  [LeadStatus.CONTACTED]: 'bg-amber-100 text-amber-800',
  [LeadStatus.ONBOARDED]: 'bg-green-100 text-green-700',
  [LeadStatus.CLOSED]: 'bg-gray-100 text-gray-500',
};

const STATUS_LABEL: Record<LeadStatus, string> = {
  [LeadStatus.PENDING_VERIFICATION]: 'Pending verification',
  [LeadStatus.NEW]: 'New',
  [LeadStatus.CONTACTED]: 'Contacted',
  [LeadStatus.ONBOARDED]: 'Onboarded',
  [LeadStatus.CLOSED]: 'Closed',
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function LeadStatusBadge({ lead }: { lead: PlatformLead }) {
  // A contacted lead carrying reviewer feedback is really "changes requested".
  const changesRequested =
    lead.status === LeadStatus.CONTACTED && !!lead.reviewFeedback;
  const label = changesRequested
    ? 'Changes requested'
    : STATUS_LABEL[lead.status] ?? lead.status;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        STATUS_BADGE[lead.status] ?? 'bg-gray-100 text-gray-500',
      )}
    >
      {label}
    </span>
  );
}

// ── Lead detail panel ──────────────────────────────────────────────────────────

/**
 * The explicit decision layer that replaces the old status <select>. Renders for
 * actionable leads (new / contacted): Approve & onboard (confirmed convert),
 * Request changes (feedback), Reject (reason). Converted/closed leads render a
 * read-only summary instead (see LeadDetail).
 */
function DecisionBar({
  lead,
  onConverted,
}: {
  lead: PlatformLead;
  onConverted: (result: ApproveLeadResult) => void;
}) {
  const approve = useApproveLead();
  const requestChanges = useRequestLeadChanges();
  const reject = useRejectLead();

  const [mode, setMode] = useState<'idle' | 'request-changes' | 'reject'>('idle');
  const [text, setText] = useState('');
  const [confirmingApprove, setConfirmingApprove] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const pending =
    approve.isPending || requestChanges.isPending || reject.isPending;
  const trimmed = text.trim();

  function switchMode(next: 'idle' | 'request-changes' | 'reject') {
    setMode(next);
    setText('');
    setActionError(null); // don't let a stale error bleed across actions
  }

  function submitApprove() {
    setActionError(null);
    approve.mutate(lead.id, {
      onSuccess: (result) => {
        setConfirmingApprove(false);
        onConverted(result);
      },
      onError: (err) => setActionError(extractErrorMessage(err)),
    });
  }

  function submitRequestChanges() {
    if (!trimmed) return;
    setActionError(null);
    requestChanges.mutate(
      { id: lead.id, feedback: trimmed },
      {
        onSuccess: () => switchMode('idle'),
        onError: (err) => setActionError(extractErrorMessage(err)),
      },
    );
  }

  function submitReject() {
    if (!trimmed) return;
    setActionError(null);
    reject.mutate(
      { id: lead.id, reason: trimmed },
      { onError: (err) => setActionError(extractErrorMessage(err)) },
    );
  }

  return (
    <section aria-label="Review decision">
      <h4 className="text-sm font-semibold text-gray-900 mb-2">Review decision</h4>

      {mode === 'idle' && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            onClick={() => setConfirmingApprove(true)}
            disabled={pending}
          >
            Approve &amp; onboard
          </Button>
          <Button
            variant="secondary"
            onClick={() => switchMode('request-changes')}
            disabled={pending}
          >
            Request changes
          </Button>
          <Button
            variant="danger"
            onClick={() => switchMode('reject')}
            disabled={pending}
          >
            Reject
          </Button>
        </div>
      )}

      {mode === 'request-changes' && (
        <div className="space-y-2">
          <label
            htmlFor="lead-feedback"
            className="block text-sm font-medium text-gray-700"
          >
            What does the submitter need to change?
          </label>
          <textarea
            id="lead-feedback"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-describedby={actionError ? 'lead-action-error' : undefined}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            placeholder="e.g. Please add a valid business license."
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={submitRequestChanges}
              disabled={!trimmed || pending}
              isLoading={requestChanges.isPending}
            >
              Send feedback
            </Button>
            <Button
              variant="ghost"
              onClick={() => switchMode('idle')}
              disabled={pending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mode === 'reject' && (
        <div className="space-y-2">
          <label
            htmlFor="lead-reason"
            className="block text-sm font-medium text-gray-700"
          >
            Reason for rejecting this submission
          </label>
          <textarea
            id="lead-reason"
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-describedby={actionError ? 'lead-action-error' : undefined}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="e.g. Outside our current service area."
          />
          <div className="flex items-center gap-2">
            <Button
              variant="danger"
              onClick={submitReject}
              disabled={!trimmed || pending}
              isLoading={reject.isPending}
            >
              Reject submission
            </Button>
            <Button
              variant="ghost"
              onClick={() => switchMode('idle')}
              disabled={pending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {actionError && (
        <p id="lead-action-error" className="mt-2 text-sm text-red-600" role="alert">
          {actionError}
        </p>
      )}

      <ConfirmDialog
        isOpen={confirmingApprove}
        variant="primary"
        title="Approve &amp; onboard"
        message={`Create an owner account and a draft property for ${lead.propertyName}? The owner will be invited to complete onboarding.`}
        confirmLabel="Approve"
        isLoading={approve.isPending}
        onConfirm={submitApprove}
        onCancel={() => {
          if (!approve.isPending) {
            setConfirmingApprove(false);
            setActionError(null);
          }
        }}
      />
    </section>
  );
}

function LeadDetail({ lead, onClose }: { lead: PlatformLead; onClose: () => void }) {
  // Holds the fresh convert result so the converted summary renders immediately
  // (before the list invalidation refetch lands), avoiding a flash.
  const [converted, setConverted] = useState<ApproveLeadResult | null>(null);

  const convertedPropertyId =
    converted?.propertyId ?? lead.convertedPropertyId ?? null;
  const effectiveStatus = converted ? LeadStatus.ONBOARDED : lead.status;
  const isActionable =
    !convertedPropertyId &&
    (effectiveStatus === LeadStatus.NEW ||
      effectiveStatus === LeadStatus.CONTACTED);

  return (
    <SlideOver isOpen onClose={onClose} title="Listing submission" width="max-w-xl">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{lead.propertyName}</h3>
              <p className="text-sm text-gray-500">{lead.ownerName}</p>
            </div>
            <LeadStatusBadge
              lead={converted ? { ...lead, status: LeadStatus.ONBOARDED } : lead}
            />
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-sm text-gray-600">
            <MapPin size={14} className="text-gray-400" aria-hidden="true" />
            {lead.city}
            {lead.country ? `, ${lead.country}` : ''}
          </div>
        </div>

        {/* Contact + facts */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-200 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              <Mail size={12} aria-hidden="true" /> Email
            </div>
            <a href={`mailto:${lead.email}`} className="text-sm text-brand-700 hover:underline break-all">
              {lead.email}
            </a>
          </div>
          <div className="rounded-lg border border-gray-200 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              <Phone size={12} aria-hidden="true" /> Phone
            </div>
            <a href={`tel:${lead.phone}`} className="text-sm text-brand-700 hover:underline">
              {lead.phone}
            </a>
          </div>
          <div className="rounded-lg border border-gray-200 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              <Bed size={12} aria-hidden="true" /> Type / rooms
            </div>
            <p className="text-sm text-gray-700 capitalize">
              {lead.propertyType} · ~{lead.roomsEstimate} rooms
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 px-4 py-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
              <Calendar size={12} aria-hidden="true" /> Submitted
            </div>
            <p className="text-sm text-gray-700">{formatDate(lead.createdAt)}</p>
          </div>
        </div>

        {/* Message */}
        <section>
          <h4 className="text-sm font-semibold text-gray-900 mb-1.5">Message</h4>
          {lead.message ? (
            <p className="text-sm text-gray-600 whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              {lead.message}
            </p>
          ) : (
            <p className="text-sm text-gray-400">No message provided.</p>
          )}
        </section>

        {/* Prior reviewer note (feedback / rejection reason), when present. */}
        {lead.reviewFeedback && effectiveStatus !== LeadStatus.CLOSED && (
          <section>
            <h4 className="text-sm font-semibold text-gray-900 mb-1.5">
              Reviewer note
            </h4>
            <p className="text-sm text-amber-800 whitespace-pre-wrap rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              {lead.reviewFeedback}
            </p>
          </section>
        )}

        {/* Decision layer / read-only outcome */}
        {isActionable ? (
          <DecisionBar lead={lead} onConverted={setConverted} />
        ) : convertedPropertyId ? (
          <section className="rounded-lg border border-green-200 bg-green-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-green-800">
              <CheckCircle2 size={16} aria-hidden="true" /> Onboarded
            </div>
            <p className="mt-1 text-sm text-green-700">
              A draft property and owner account were created
              {converted ? ' just now' : ` on ${formatDate(lead.updatedAt)}`}.
            </p>
            <Link
              to={`/admin/properties/${convertedPropertyId}`}
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-green-800 hover:underline"
            >
              View property <ExternalLink size={14} aria-hidden="true" />
            </Link>
          </section>
        ) : effectiveStatus === LeadStatus.CLOSED ? (
          <section className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <XCircle size={16} aria-hidden="true" /> Rejected
            </div>
            {lead.reviewFeedback ? (
              <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                {lead.reviewFeedback}
              </p>
            ) : (
              <p className="mt-1 text-sm text-gray-500">No reason recorded.</p>
            )}
          </section>
        ) : (
          <section className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-sm text-gray-500">
              This submission is not yet actionable.
            </p>
          </section>
        )}
      </div>
    </SlideOver>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AdminLeadsPage() {
  const [status, setStatus] = useState<LeadStatus | 'all'>('all');
  const [selected, setSelected] = useState<PlatformLead | null>(null);
  const { data, isLoading, isError, refetch } = usePlatformLeads(status);

  const leads = data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Owner Leads"
        description="Owners who submitted interest in listing a property — contact them and onboard."
      />

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Lead status filter">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={status === tab.value}
            onClick={() => setStatus(tab.value)}
            className={cn(
              'px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              status === tab.value
                ? 'bg-brand-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading submissions…</span>
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
            <p className="text-sm text-red-700 mb-4">Unable to load submissions.</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : leads.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No submissions"
            description="Leads from the storefront will appear here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100" aria-label="Listing submissions">
              <thead>
                <tr className="bg-gray-50">
                  {['Owner', 'Contact', 'Property', 'City', 'Type', 'Rooms', 'Status', 'Date', ''].map(
                    (col, i) => (
                      <th
                        key={col || `col-${i}`}
                        scope="col"
                        className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leads.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => setSelected(lead)}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {lead.ownerName}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500">
                      <div className="truncate max-w-[180px]">{lead.email}</div>
                      <div className="text-xs text-gray-400">{lead.phone}</div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {lead.propertyName}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {lead.city}
                      {lead.country ? `, ${lead.country}` : ''}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 capitalize">{lead.propertyType}</td>
                    <td className="px-5 py-4 text-sm text-gray-500">{lead.roomsEstimate}</td>
                    <td className="px-5 py-4">
                      <LeadStatusBadge lead={lead} />
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(lead.createdAt)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <ChevronRight size={16} className="text-gray-300 inline" aria-hidden="true" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <LeadDetail
          lead={leads.find((l) => l.id === selected.id) ?? selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
